import React, { useEffect } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Typography
} from '@mui/material';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  PlaylistDialogFormValues,
  PlaylistsDialogFormProps
} from '@modules/digital-signage/model/playlists-dialog-form.model.ts';
import {
  PlaylistAnimationEnum,
  PlaylistReqModel,
  PlaylistScreenFormItem
} from '@modules/digital-signage/model/playlists.model.ts';
import PlaylistScreenRow from './components/playlist-screen-row/playlist-screen-row.tsx';
import usePlaylistsStore from '@modules/digital-signage/pages/playlists/store/playlists.store.ts';
import { parseDurationToMs, parseMsToString } from '@utils/duration.utils.ts';
import { useErrorMessage } from '@shared/hooks/use-error-message';
import { validateNotOnlySpaces } from '@shared/utils/trim-form-values-utils';
import { playlistErrorMessages } from '@modules/digital-signage/utils/digital-signage.utils';
import { ICONS } from '@components/icons/icon.tsx';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';

const PlaylistsDialogForm: React.FC<PlaylistsDialogFormProps> = ({
  selectedItem,
  screensData,
  onSave
}) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { isLoading } = usePlaylistsStore();

  const methods = useForm<PlaylistDialogFormValues>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      screens: [
        {
          id: crypto.randomUUID(),
          screen: null,
          visibleDuration: '',
          animation: PlaylistAnimationEnum.None
        }
      ]
    }
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = methods;

  const { fields, append, remove, move } =
    useFieldArray<PlaylistDialogFormValues>({
      control,
      name: 'screens'
    });

  const watchedScreens = useWatch({ control, name: 'screens' });
  const hasValidRow = watchedScreens?.some(
    (s) => s.screen != null && s.animation
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    if (selectedItem) {
      const screensFormItems: PlaylistScreenFormItem[] = (
        selectedItem.screens || []
      ).map((s) => ({
        id: crypto.randomUUID(),
        screen: screensData.find((sc) => sc.id === s.screen?._id) || null,
        visibleDuration: parseMsToString(s.visibleDuration),
        animation:
          s.animation === PlaylistAnimationEnum.Fade
            ? PlaylistAnimationEnum.Fade
            : PlaylistAnimationEnum.None
      }));
      reset({
        name: selectedItem.name,
        screens: screensFormItems.length
          ? screensFormItems
          : [
              {
                id: crypto.randomUUID(),
                screen: null,
                visibleDuration: '',
                animation: PlaylistAnimationEnum.None
              }
            ]
      });
    } else {
      reset({
        name: '',
        screens: [
          {
            id: crypto.randomUUID(),
            screen: null,
            visibleDuration: '',
            animation: PlaylistAnimationEnum.None
          }
        ]
      });
    }
  }, [selectedItem, screensData, reset]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) move(oldIndex, newIndex);
  };

  const handleAddScreen = () => {
    append({
      id: crypto.randomUUID(),
      screen: null,
      visibleDuration: '',
      animation: PlaylistAnimationEnum.None
    });
  };

  const onSubmit = (data: PlaylistDialogFormValues) => {
    const reqScreens = data.screens
      .filter((s) => s.screen)
      .map((s) => ({
        screen: s.screen!.id,
        visibleDuration: parseDurationToMs(s.visibleDuration),
        animation: s.animation
      }));

    const body: PlaylistReqModel = {
      name: data.name.trim(),
      screens: reqScreens
    };

    onSave(body);
  };

  return (
    <FormProvider {...methods}>
      <form className="min-h-213.5 max-lg-xl:min-h-163.25 flex flex-col justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <FormControl error={!!errors.name} fullWidth>
            <FormLabel>
              <span className="reuqired-symbole">*</span>
              {t('column_label_name')}
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              error={!!errors.name}
              placeholder={t('placeholder_name')}
            />
            <FormHelperText>
              {errors?.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages: playlistErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <div className="flex flex-col gap-3 p-3.5 border border-(--blue-12) rounded-xl">
            <div className="w-full flex justify-between items-center">
              <div>
                <Typography
                  variant="body1"
                  className="text-(--dark-2)! font-semibold!"
                >
                  {t('digital_signage_playlist_screen_rotation_label')}
                </Typography>
                <Typography
                  variant="caption"
                  className="text-(--blue-4)! font-normal!"
                >
                  {t('digital_signage_playlist_dnd_hint')}
                </Typography>
              </div>

              <InfoTooltip text="tooltip_info_playlist_duration" />
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-2">
                  {fields.map((field, index) => (
                    <PlaylistScreenRow
                      key={field.id}
                      fieldId={field.id}
                      index={index}
                      screens={screensData}
                      onRemove={remove}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <Button
              className="max-w-150! border-(--primary)! text-(--primary)! [&_svg_path]:stroke-(--primary)! rounded-[7px]! max-lg-xl:max-w-100!"
              variant="outlined"
              startIcon={ICONS.plusIcon}
              onClick={handleAddScreen}
            >
              {t('add_screen')}
            </Button>
          </div>
        </div>

        <div className="flex justify-end pt-2 sticky bottom-0 bg-(--white) pb-1">
          <Button
            variant="contained"
            disabled={!isValid || !hasValidRow}
            loading={isLoading()}
            onClick={handleSubmit(onSubmit)}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default PlaylistsDialogForm;
